import React, { useState, useEffect } from "react";
import "./CharacterEditModal.css";

const CharacterEditModal = ({ character, onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState("info");
  const [editedCharacter, setEditedCharacter] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (character) {
      // Transform backend data structure to match modal expectations
      const transformed = JSON.parse(JSON.stringify(character));

      // Transform characterInfo field names from English to Italian
      if (transformed.characterInfo) {
        const info = transformed.characterInfo;
        transformed.characterInfo = {
          nome: info.name || "",
          eta: info.age || "",
          sesso: info.sex || "",
          lavoro: info.work || "",
          residenza: info.residence || "",
          luogoDiNascita: info.bornage || "",
        };
      }

      // Transform characteristics array to object
      if (Array.isArray(transformed.characteristics)) {
        const charObj = {};
        transformed.characteristics.forEach((char) => {
          charObj[char.name] = char.value || 0;
        });
        transformed.characteristics = charObj;
      }

      // Transform points array to object
      if (Array.isArray(transformed.points)) {
        const pointsObj = {};
        transformed.points.forEach((point) => {
          if (point.name === "Punti Ferita") {
            pointsObj.currentSanita = point.value || 0;
            pointsObj.maxSanita = point.maxValue || 0;
          } else if (point.name === "Punti Fortuna") {
            pointsObj.currentFortuna = point.value || 0;
            pointsObj.maxFortuna = point.maxValue || 0;
          } else if (point.name === "Punti Magia") {
            pointsObj.currentMagia = point.value || 0;
            pointsObj.maxMagia = point.maxValue || 0;
          }
        });
        transformed.points = pointsObj;
      }

      setEditedCharacter(transformed);
    }
  }, [character]);

  if (!editedCharacter) return null;

  const handleSave = async () => {
    setLoading(true);
    try {
      // Transform data back to backend format
      const backendFormat = JSON.parse(JSON.stringify(editedCharacter));

      // Transform characterInfo from Italian to English field names
      if (backendFormat.characterInfo) {
        const info = backendFormat.characterInfo;
        backendFormat.characterInfo = {
          name: info.nome || "",
          age: info.eta || "",
          sex: info.sesso || "",
          work: info.lavoro || "",
          residence: info.residenza || "",
          bornage: info.luogoDiNascita || "",
        };
      }

      // Transform characteristics object to array
      if (
        backendFormat.characteristics &&
        typeof backendFormat.characteristics === "object" &&
        !Array.isArray(backendFormat.characteristics)
      ) {
        const charArray = [];
        Object.entries(backendFormat.characteristics).forEach(
          ([key, value]) => {
            charArray.push({
              name: key,
              value: value || 0,
            });
          },
        );
        backendFormat.characteristics = charArray;
      }

      // Transform points object to array
      if (
        backendFormat.points &&
        typeof backendFormat.points === "object" &&
        !Array.isArray(backendFormat.points)
      ) {
        const pointsArray = [];
        if (
          backendFormat.points.currentSanita !== undefined ||
          backendFormat.points.maxSanita !== undefined
        ) {
          pointsArray.push({
            name: "Punti Ferita",
            value: backendFormat.points.currentSanita || 0,
            maxValue: backendFormat.points.maxSanita || 0,
          });
        }
        if (
          backendFormat.points.currentFortuna !== undefined ||
          backendFormat.points.maxFortuna !== undefined
        ) {
          pointsArray.push({
            name: "Punti Fortuna",
            value: backendFormat.points.currentFortuna || 0,
            maxValue: backendFormat.points.maxFortuna || 0,
          });
        }
        if (
          backendFormat.points.currentMagia !== undefined ||
          backendFormat.points.maxMagia !== undefined
        ) {
          pointsArray.push({
            name: "Punti Magia",
            value: backendFormat.points.currentMagia || 0,
            maxValue: backendFormat.points.maxMagia || 0,
          });
        }
        backendFormat.points = pointsArray;
      }

      await onSave(backendFormat);
      onClose();
    } catch (error) {
      console.error("Error saving character:", error);
      alert("Errore nel salvataggio del personaggio");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (path, value) => {
    const keys = path.split(".");
    const updated = { ...editedCharacter };
    let current = updated;

    for (let i = 0; i < keys.length - 1; i++) {
      current[keys[i]] = { ...current[keys[i]] };
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
    setEditedCharacter(updated);
  };

  const getValue = (path) => {
    const keys = path.split(".");
    let value = editedCharacter;
    for (const key of keys) {
      value = value?.[key];
    }
    return value ?? "";
  };

  const renderInfoTab = () => (
    <div className="modal-tab-content">
      <div className="form-grid">
        <div className="form-group">
          <label>Nome Giocatore</label>
          <input
            type="text"
            value={editedCharacter.playerName || ""}
            onChange={(e) =>
              setEditedCharacter({
                ...editedCharacter,
                playerName: e.target.value,
              })
            }
          />
        </div>

        <div className="form-group">
          <label>Nome Personaggio</label>
          <input
            type="text"
            value={getValue("characterInfo.nome")}
            onChange={(e) => updateField("characterInfo.nome", e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Età</label>
          <input
            type="text"
            value={getValue("characterInfo.eta")}
            onChange={(e) => updateField("characterInfo.eta", e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Sesso</label>
          <select
            value={getValue("characterInfo.sesso")}
            onChange={(e) => updateField("characterInfo.sesso", e.target.value)}
          >
            <option value="">Seleziona</option>
            <option value="M">Maschio</option>
            <option value="F">Femmina</option>
            <option value="Altro">Altro</option>
          </select>
        </div>

        <div className="form-group">
          <label>Lavoro</label>
          <input
            type="text"
            value={getValue("characterInfo.lavoro")}
            onChange={(e) =>
              updateField("characterInfo.lavoro", e.target.value)
            }
          />
        </div>

        <div className="form-group">
          <label>Luogo di Nascita</label>
          <input
            type="text"
            value={getValue("characterInfo.luogoDiNascita")}
            onChange={(e) =>
              updateField("characterInfo.luogoDiNascita", e.target.value)
            }
          />
        </div>

        <div className="form-group full-width">
          <label>Residenza</label>
          <input
            type="text"
            value={getValue("characterInfo.residenza")}
            onChange={(e) =>
              updateField("characterInfo.residenza", e.target.value)
            }
          />
        </div>
      </div>
    </div>
  );

  const renderCharacteristicsTab = () => {
    const characteristics = [
      "Forza",
      "Costituzione",
      "Taglia",
      "Destrezza",
      "Fascino",
      "Istruzione",
      "Intelligenza",
      "Potere",
    ];

    return (
      <div className="modal-tab-content">
        <div className="characteristics-grid">
          {characteristics.map((char) => (
            <div key={char} className="characteristic-item">
              <label>{char}</label>
              <input
                type="number"
                value={getValue(`characteristics.${char}`)}
                onChange={(e) =>
                  updateField(
                    `characteristics.${char}`,
                    parseInt(e.target.value) || 0,
                  )
                }
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPointsTab = () => (
    <div className="modal-tab-content">
      <div className="form-grid">
        <div className="form-group">
          <label>Punti Sanità Attuali</label>
          <input
            type="number"
            value={getValue("points.currentSanita")}
            onChange={(e) =>
              updateField("points.currentSanita", parseInt(e.target.value) || 0)
            }
          />
        </div>

        <div className="form-group">
          <label>Punti Sanità Massimi</label>
          <input
            type="number"
            value={getValue("points.maxSanita")}
            onChange={(e) =>
              updateField("points.maxSanita", parseInt(e.target.value) || 0)
            }
          />
        </div>

        <div className="form-group">
          <label>Punti Fortuna Attuali</label>
          <input
            type="number"
            value={getValue("points.currentFortuna")}
            onChange={(e) =>
              updateField(
                "points.currentFortuna",
                parseInt(e.target.value) || 0,
              )
            }
          />
        </div>

        <div className="form-group">
          <label>Punti Fortuna Massimi</label>
          <input
            type="number"
            value={getValue("points.maxFortuna")}
            onChange={(e) =>
              updateField("points.maxFortuna", parseInt(e.target.value) || 0)
            }
          />
        </div>

        <div className="form-group">
          <label>Punti Magia Attuali</label>
          <input
            type="number"
            value={getValue("points.currentMagia")}
            onChange={(e) =>
              updateField("points.currentMagia", parseInt(e.target.value) || 0)
            }
          />
        </div>

        <div className="form-group">
          <label>Punti Magia Massimi</label>
          <input
            type="number"
            value={getValue("points.maxMagia")}
            onChange={(e) =>
              updateField("points.maxMagia", parseInt(e.target.value) || 0)
            }
          />
        </div>
      </div>
    </div>
  );

  const renderSkillsTab = () => {
    const skills = editedCharacter.skills || [];

    return (
      <div className="modal-tab-content">
        <div className="skills-list">
          {skills.map((skill, index) => (
            <div key={index} className="skill-item">
              <label>{skill.name}</label>
              <input
                type="number"
                min={skill.minValue || 0}
                max={skill.maxValue || 100}
                value={skill.value || 0}
                onChange={(e) => {
                  const newSkills = [...skills];
                  newSkills[index] = {
                    ...newSkills[index],
                    value: parseInt(e.target.value) || 0,
                  };
                  updateField("skills", newSkills);
                }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCashTab = () => {
    const properties = editedCharacter.cash?.properties || [];

    return (
      <div className="modal-tab-content">
        <div className="form-grid">
          <div className="form-group">
            <label>Livello di Spesa (Spending Level)</label>
            <input
              type="text"
              value={getValue("cash.spendinglevel")}
              onChange={(e) =>
                updateField("cash.spendinglevel", e.target.value)
              }
              readOnly
              disabled
              style={{ opacity: 0.6, cursor: "not-allowed" }}
            />
          </div>

          <div className="form-group">
            <label>Contanti (Cash)</label>
            <input
              type="text"
              value={getValue("cash.cash")}
              onChange={(e) => updateField("cash.cash", e.target.value)}
              readOnly
              disabled
              style={{ opacity: 0.6, cursor: "not-allowed" }}
            />
          </div>

          <div className="form-group full-width">
            <label>Proprietà</label>
            <div className="properties-list">
              {properties.map((property, index) => (
                <div key={index} className="property-item">
                  <input
                    type="text"
                    value={property}
                    onChange={(e) => {
                      const newProperties = [...properties];
                      newProperties[index] = e.target.value;
                      updateField("cash.properties", newProperties);
                    }}
                    placeholder={`Proprietà ${index + 1}`}
                  />
                  <button
                    type="button"
                    className="btn-remove-property"
                    onClick={() => {
                      const newProperties = properties.filter(
                        (_, i) => i !== index,
                      );
                      updateField("cash.properties", newProperties);
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn-add-property"
                onClick={() => {
                  const newProperties = [...properties, ""];
                  updateField("cash.properties", newProperties);
                }}
              >
                + Aggiungi Proprietà
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBackgroundTab = () => {
    const backgroundFields = [
      { key: "description", label: "Descrizione" },
      { key: "ideology", label: "Ideologia e Credenze" },
      { key: "importantPeople", label: "Persone Importanti" },
      { key: "significantPlaces", label: "Luoghi Significativi" },
      { key: "valuables", label: "Beni Preziosi" },
      { key: "traits", label: "Tratti" },
      { key: "wounds", label: "Ferite e Cicatrici" },
      { key: "phobias", label: "Fobie e Manie" },
      { key: "knowledge", label: "Tomi e Incantesimi" },
      { key: "meetings", label: "Incontri con Entità Oscure" },
    ];

    return (
      <div className="modal-tab-content">
        <div className="background-grid">
          {backgroundFields.map((field) => (
            <div key={field.key} className="form-group full-width">
              <label>{field.label}</label>
              <textarea
                rows={3}
                value={getValue(`background.${field.key}`)}
                onChange={(e) =>
                  updateField(`background.${field.key}`, e.target.value)
                }
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            Modifica Personaggio:{" "}
            {editedCharacter.characterInfo?.nome ||
              character.characterInfo?.name ||
              "N/A"}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-tabs">
          <button
            className={activeTab === "info" ? "active" : ""}
            onClick={() => setActiveTab("info")}
          >
            Info Base
          </button>
          <button
            className={activeTab === "characteristics" ? "active" : ""}
            onClick={() => setActiveTab("characteristics")}
          >
            Caratteristiche
          </button>
          <button
            className={activeTab === "points" ? "active" : ""}
            onClick={() => setActiveTab("points")}
          >
            Punti
          </button>
          <button
            className={activeTab === "skills" ? "active" : ""}
            onClick={() => setActiveTab("skills")}
          >
            Abilità
          </button>
          <button
            className={activeTab === "cash" ? "active" : ""}
            onClick={() => setActiveTab("cash")}
          >
            Soldi
          </button>
          <button
            className={activeTab === "background" ? "active" : ""}
            onClick={() => setActiveTab("background")}
          >
            Background
          </button>
        </div>

        <div className="modal-body">
          {activeTab === "info" && renderInfoTab()}
          {activeTab === "characteristics" && renderCharacteristicsTab()}
          {activeTab === "points" && renderPointsTab()}
          {activeTab === "skills" && renderSkillsTab()}
          {activeTab === "cash" && renderCashTab()}
          {activeTab === "background" && renderBackgroundTab()}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-cancel">
            Annulla
          </button>
          <button onClick={handleSave} className="btn-save" disabled={loading}>
            {loading ? "Salvataggio..." : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CharacterEditModal;
